import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  FiShoppingCart,
  FiUser,
  FiSearch,
  FiX,
  FiChevronDown,
  FiHome,
  FiGrid,
  FiInfo,
  FiMail,
  FiPhone,
} from 'react-icons/fi';
import { useCart } from '../context/CartContext';
import './Navbar.css';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const menuRef = useRef(null);
  const hamburgerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { totalItems, setIsCartOpen } = useCart();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMenuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') { setIsMenuOpen(false); setActiveDropdown(null); }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    if (!isMobile && isMenuOpen) { setIsMenuOpen(false); setActiveDropdown(null); }
  }, [isMobile, isMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const menuItems = [
    { label: 'Home', to: '/', icon: <FiHome /> },
    {
      label: 'Spices',
      to: '/shop',
      icon: <FiGrid />,
      dropdown: [
        { label: 'All Spices', to: '/shop' },
        { label: 'Whole Spices', to: '/shop' },
        { label: 'Spice Blends', to: '/shop' },
        { label: 'Organic Herbs', to: '/shop' },
      ],
    },
    { label: 'About Us', to: '/about', icon: <FiInfo /> },
    { label: 'Contact', to: '/contact', icon: <FiMail /> },
  ];

  const isActive = (to) => location.pathname === to;

  const handleDropdownToggle = (label) => {
    setActiveDropdown(activeDropdown === label ? null : label);
  };

  return (
    <>
      <header className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">

          {/* Logo */}
          <div className="logo-section">
            <Link to="/" className="logo">
              <img src="/logo.png" alt="Shammy Spices" className="logo-img" />
              <div className="logo-text">
                <span className="logo-main">Shammy</span>
                <span className="logo-sub">Spices</span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="desktop-nav" aria-label="Primary navigation">
            <ul className="nav-menu">
              {menuItems.map((item, index) => (
                <li
                  key={index}
                  className="nav-item"
                  onMouseEnter={() => item.dropdown && setActiveDropdown(item.label)}
                  onMouseLeave={() => item.dropdown && setActiveDropdown(null)}
                >
                  <Link
                    to={item.to}
                    className={`nav-link ${isActive(item.to) ? 'active' : ''}`}
                  >
                    {item.label}
                    {item.dropdown && (
                      <FiChevronDown
                        className={`dropdown-icon ${activeDropdown === item.label ? 'active' : ''}`}
                      />
                    )}
                  </Link>

                  {item.dropdown && (
                    <div className={`dropdown-menu ${activeDropdown === item.label ? 'active' : ''}`}>
                      {item.dropdown.map((subItem, subIndex) => (
                        <Link
                          key={subIndex}
                          to={subItem.to}
                          className="dropdown-item"
                          onClick={() => setActiveDropdown(null)}
                        >
                          <span className="dropdown-item-text">{subItem.label}</span>
                          {subItem.badge && (
                            <span className="dropdown-badge">{subItem.badge}</span>
                          )}
                        </Link>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/* Desktop Action Buttons */}
          <div className="desktop-actions">
            <button className="action-btn search-btn" aria-label="Search"><FiSearch /></button>
            <button className="action-btn user-btn" aria-label="Account"><FiUser /></button>
            <button className="action-btn cart-btn" aria-label="Cart" onClick={() => setIsCartOpen(true)}>
              <FiShoppingCart />
              {totalItems > 0 && <span className="cart-badge">{totalItems}</span>}
            </button>
            <button className="shop-now-btn" onClick={() => navigate('/shop')}>Shop Now</button>
          </div>

          {/* Mobile Actions */}
          <div className="mobile-actions">
            <button className="mobile-action-btn cart-btn" aria-label="Cart" onClick={() => setIsCartOpen(true)}>
              <FiShoppingCart />
              {totalItems > 0 && <span className="mobile-cart-badge">{totalItems}</span>}
            </button>
            <button
              ref={hamburgerRef}
              className={`hamburger-btn ${isMenuOpen ? 'open' : ''}`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              <div className="hamburger-icon">
                <span className="hamburger-line"></span>
                <span className="hamburger-line"></span>
                <span className="hamburger-line"></span>
              </div>
            </button>
          </div>

        </div>
      </header>

      {/* Backdrop */}
      <div
        className={`menu-backdrop ${isMenuOpen ? 'open' : ''}`}
        onClick={() => setIsMenuOpen(false)}
        role="presentation"
        aria-hidden="true"
      />

      {/* Mobile Drawer */}
      <div
        ref={menuRef}
        className={`mobile-menu-overlay ${isMenuOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Main menu"
      >
        <div className="mobile-menu-container">

          <div className="mobile-menu-header">
            <Link to="/" className="mobile-menu-logo" onClick={() => setIsMenuOpen(false)}>
              <img src="/logo.png" alt="Shammy Spices" className="mobile-logo-img" />
              <div className="mobile-logo-text">
                <span className="mobile-logo-main">Shammy</span>
                <span className="mobile-logo-sub">Spices</span>
              </div>
            </Link>
            <button
              className="mobile-close-btn"
              onClick={() => setIsMenuOpen(false)}
              aria-label="Close menu"
            >
              <FiX />
            </button>
          </div>

          <div className="mobile-search-container">
            <div className="mobile-search">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search spices, recipes..."
                className="search-input"
                aria-label="Search"
              />
            </div>
          </div>

          <nav className="mobile-nav" aria-label="Mobile navigation">
            <ul className="mobile-menu-list">
              {menuItems.map((item, index) => (
                <li
                  key={index}
                  className="mobile-menu-item"
                  style={{ '--index': index }}
                >
                  <div className="mobile-menu-item-inner">
                    {item.dropdown ? (
                      <button
                        className={`mobile-menu-link has-dropdown ${isActive(item.to) ? 'active' : ''}`}
                        onClick={() => handleDropdownToggle(item.label)}
                        aria-expanded={activeDropdown === item.label}
                      >
                        <span className="mobile-link-icon">{item.icon}</span>
                        <span className="mobile-link-text">{item.label}</span>
                        <FiChevronDown
                          className={`mobile-dropdown-icon ${activeDropdown === item.label ? 'active' : ''}`}
                        />
                      </button>
                    ) : (
                      <Link
                        to={item.to}
                        className={`mobile-menu-link ${isActive(item.to) ? 'active' : ''}`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <span className="mobile-link-icon">{item.icon}</span>
                        <span className="mobile-link-text">{item.label}</span>
                      </Link>
                    )}

                    {item.dropdown && (
                      <div className={`mobile-dropdown ${activeDropdown === item.label ? 'active' : ''}`}>
                        {item.dropdown.map((subItem, subIndex) => (
                          <Link
                            key={subIndex}
                            to={subItem.to}
                            className="mobile-dropdown-item"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <span className="mobile-dropdown-text">{subItem.label}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </nav>

          <div className="mobile-menu-footer">
            <div className="mobile-account-actions">
              <button className="mobile-account-btn">
                <FiUser /><span>My Account</span>
              </button>
            </div>

            <div className="mobile-contact">
              <p className="contact-title">Need help?</p>
              <a href="tel:+255655778989" className="contact-phone">
                <FiPhone /><span>(+255) 655 778 989</span>
              </a>
              <p className="contact-hours">Mon–Sun: 9AM – 6PM EAT</p>
            </div>

            <button
              className="mobile-shop-btn"
              onClick={() => { navigate('/shop'); setIsMenuOpen(false); }}
            >
              Shop All Products
            </button>
          </div>

        </div>
      </div>
    </>
  );
};

export default Navbar;
